import React, {useState, useContext, useEffect, useRef} from "react";
import { useTranslation } from 'react-i18next';
import { FirebaseAuth, CloudFunctions } from "../../../../components/FirebaseAuth/firebase";
import { AuthContext } from "../../../../components/FirebaseAuth";
import { BreadcrumbContext } from '../../../../components/Breadcrumb';
import Loader from '../../../../components/Loader';
import CheckIcon from '@mui/icons-material/Check';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import countryJSON from "../../../../inc/country.json";
import { Box, Grid, Card, CardHeader, CardContent, CardActions, Button, Divider, Container, Autocomplete, TextField, Stack, Alert, Typography } from "@mui/material";
import { localizeBackendError } from "../../../../libs/backendError";

const cardSx = {
    backgroundColor: (theme) => theme.custom.card,
    boxShadow: (theme) => theme.custom.cardShadow,
    border: (theme) => `1px solid ${theme.custom.border}`,
};

const Plans = () => {
    const { t } = useTranslation();

    const countries = countryJSON.countries;

    const { userData, authUser } = useContext(AuthContext);
    const stripe = useStripe();
    const elements = useElements();
    const mountedRef = useRef(true);
    const { setBreadcrumb } = useContext(BreadcrumbContext);

    const CARD_ELEMENT_OPTIONS = {
        style: {
            base: {
              color: '#32325d',
              fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
              fontSmoothing: 'antialiased',
              fontSize: '16px',
              '::placeholder': {
                color: '#aab7c4'
              }
            },
            invalid: {
              color: '#fa755a',
              iconColor: '#fa755a'
            }
        },
        hidePostalCode: true
    };

    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState({id: 0});
    const [cardError, setCardError] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [country, setCountry] = useState("");
    const [countryError, setCountryError] = useState(null);
    const [state, setState] = useState("");
    const [states, setStates] = useState([]);
    const [stateError, setStateError] = useState(null);

    useEffect(() => {
        setBreadcrumb([
            {
                to: "/",
                text: t('common.home'),
                active: false
            },
            {
                to: "/account/"+userData.currentAccount.id+"/",
                text: userData.currentAccount.name,
                active: false
            },
            {
                to: null,
                text: t('plans.title'),
                active: true
            }
        ]);
        setLoading(true);

        const plansQuery = FirebaseAuth.firestore().collection('plans').orderBy('price', 'asc');
        plansQuery.get().then(planSnapShots => {
            if (!mountedRef.current) return null
            let p = [];
            planSnapShots.forEach(doc => {
                if(doc.data().currency === 'usd' && doc.data().enabled){
                    p.push({
                        'id': doc.id,
                        'name': doc.data().name,
                        'price': doc.data().price,
                        'pricingMessage': doc.data().pricingMessage,
                        'currency': doc.data().currency,
                        'paymentCycle': doc.data().paymentCycle,
                        'features': doc.data().features,
                        'stripePriceId': doc.data().stripePriceId,
                        'current': (userData.currentAccount.planId===doc.id)?true:false
                    });
                }

            });
            if(p.length > 0){
                const ascendingOrderPlans = p.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
                setPlans(ascendingOrderPlans);
            }
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[userData, setBreadcrumb, t]);

    useEffect(() => {
        return () => {
            mountedRef.current = false
        }
    },[]);

    const subscribe = async(event) => {
        event.preventDefault();
        setErrorMessage(null);

        if(!authUser.user.emailVerified){
            setErrorMessage(t('plans.emailVerifyRequired'));
            return;
        }

        setProcessing(true);

        let hasError = false;
        let paymentMethodId = '';

        if(selectedPlan.price !== 0){
            if(country === ''){
                setCountryError(t('plans.countryRequired'));
                hasError = true;
            }

            if(state === '' && countries[country] && countries[country].states){
                setStateError(t('plans.stateRequired'));
                hasError = true;
            }

            setCardError(null);

            if (!stripe || !elements) {
                // Stripe.js has not loaded yet. Make sure to disable
                // form submission until Stripe.js has loaded.
                return;
            }

            // Get a reference to a mounted CardElement. Elements knows how
            // to find your CardElement because there can only ever be one of
            // each type of element.
            const cardElement = elements.getElement(CardElement);

            // Use your card Element with other Stripe.js APIs
            const {error, paymentMethod} = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement
            });

            if (error) {
                setCardError(error.message);
                hasError = true;
            } else {
                paymentMethodId = paymentMethod.id;
            }
        }


        if(!hasError){
            const createSubscription = CloudFunctions.httpsCallable('createSubscription');
            createSubscription({
                planId: selectedPlan.id,
                accountId: userData.currentAccount.id,
                paymentMethodId: paymentMethodId,
                billing: {
                    country: country,
                    state: state
                }
            }).then(res => {
                // physical page load to reload the account data
                if (!mountedRef.current) return null
                if (userData.currentAccount.planId === null) {
                    // new
                    if(selectedPlan.price === 0){
                        document.location = '/account/'+userData.currentAccount.id+'/installation/#new-free';
                    }else{
                        document.location = '/account/'+userData.currentAccount.id+'/installation/#new-paid';
                    }
                }else{
                    // change plan
                    document.location = '/account/'+userData.currentAccount.id+'/#change';
                }
            }).catch(err => {
                if (!mountedRef.current) return null
                setProcessing(false);
                setErrorMessage(localizeBackendError(err, t));
            });
        }else{
            setProcessing(false);
        }
    }

    return (
        <Container disableGutters>
        {(!loading)?(
            <>{(userData.currentAccount.owner === authUser.user.uid)?(
            <>{plans.length > 0 ? (
            <Card sx={cardSx}>
                <Box p={3} style={{textAlign: 'center'}} >
                    <Typography variant="h4" sx={{ mb: 3 }}>{t('plans.title')}</Typography>
                    <Grid container spacing={3}>

                            <>
                            {plans.map((plan,i) =>
                                <Grid container item xs={12} md={6} key={i} >
                                    <Card sx={{
                                        ...cardSx,
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        paddingBottom: '20px',
                                    }}>
                                        <CardHeader title={plan.name} subheader={plan.pricingMessage} />
                                        <CardContent>
                                            <Divider sx={{ borderColor: (theme) => theme.custom.border }} />
                                            <ul style={{listStyleType: 'none', paddingLeft: '0px'}}>
                                            {plan.features.map((feature, i) =>
                                                <li key={i} style={{paddingTop: '10px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                    <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} /> {feature}
                                                </li>
                                            )}
                                            </ul>
                                        </CardContent>
                                        <CardActions style={{
                                            marginTop: 'auto',
                                            justifyContent: 'center',
                                        }}>
                                            {plan.current?(
                                                <Button color="info" variant="contained" disabled={true}>{t('plans.current')}</Button>
                                            ):(
                                                <>
                                                    <Button color="info" variant={(plan.id!==selectedPlan.id)?"outlined":"contained"} onClick={() => {
                                                        for(let i=0; i<plans.length; i++){
                                                            if(plans[i].id === plan.id){
                                                                setSelectedPlan(plan);
                                                            }
                                                        }
                                                    }}>{plan.id===selectedPlan.id && <CheckIcon sx={{ fontSize: 18, mr: 0.5 }} />}{(plan.id!==selectedPlan.id)?t('plans.select'):t('plans.selected')}</Button>
                                                </>
                                            )}
                                        </CardActions>
                                    </Card>
                                </Grid>
                            )}
                            </>

                    </Grid>
                    {selectedPlan.id !== 0 && selectedPlan.price > 0 &&
                        <div style={{justifyContent: 'center', marginTop: '50px'}}>
                            <Typography variant="h5" sx={{ mb: 2 }}>{t('plans.billingDetails')}</Typography>
                            <Grid container spacing={3}>
                                <Grid container item xs={12}>
                                    <Card sx={{
                                        ...cardSx,
                                        width: '100%',
                                        paddingBottom: '20px',
                                    }}>
                                        <CardContent>
                                            <Container maxWidth="sm">
                                                <Stack spacing={3}>
                                                    {countryError !== null &&
                                                        <Alert severity="error" onClose={() => setCountryError(null)}>{countryError}</Alert>
                                                    }
                                                    <Autocomplete
                                                        value={(country !== '')?(countries.find(obj =>{
                                                            return obj.code === country
                                                        })):(null)}
                                                        options={countries}
                                                        autoHighlight
                                                        getOptionLabel={(option) => option.label}
                                                        renderOption={(props, option) => (
                                                            <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
                                                                <img
                                                                    loading="lazy"
                                                                    width="20"
                                                                    src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                                                    srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                                                                    alt=""
                                                                />
                                                                {option.label}
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={t('plans.country')}
                                                                inputProps={{
                                                                    ...params.inputProps,
                                                                    autoComplete: 'new-password',
                                                                }}
                                                            />
                                                        )}
                                                        onChange={(event, newValue) => {
                                                            if(newValue && newValue.code){
                                                                setCountry(newValue.code);
                                                                setState("");
                                                                if(newValue.states){
                                                                    setStates(newValue.states);
                                                                }else{
                                                                    setStates([]);
                                                                }
                                                                setCountryError(null);
                                                            }
                                                        }}
                                                    />
                                                    {states.length > 0 &&
                                                    <>
                                                        {stateError !== null &&
                                                            <Alert severity="error" onClose={() => setStateError(null)}>{stateError}</Alert>
                                                        }
                                                        <Autocomplete
                                                            value={(state !== '')?(states.find(obj =>{
                                                                return obj.code === state
                                                            })):(null)}
                                                            options={states}
                                                            autoHighlight
                                                            getOptionLabel={(option) => option.label}
                                                            renderOption={(props, option) => (
                                                                <Box component="li" {...props}>
                                                                    {option.label}
                                                                </Box>
                                                            )}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={t('plans.state')}
                                                                    inputProps={{
                                                                        ...params.inputProps,
                                                                        autoComplete: 'new-password',
                                                                    }}
                                                                />
                                                            )}
                                                            onChange={(event, newValue) => {
                                                                if(newValue && newValue.code){
                                                                    setState(newValue.code);
                                                                    setStateError(null);
                                                                }

                                                            }}
                                                        />
                                                    </>
                                                    }
                                                    {cardError !== null &&
                                                        <Alert severity="error" onClose={() => setCardError(null)}>{cardError}</Alert>
                                                    }
                                                    <div style={{position: "relative", minHeight: '56px', padding: '15px'}}>
                                                        <CardElement options={CARD_ELEMENT_OPTIONS}></CardElement>
                                                        <fieldset style={{
                                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                                            borderStyle: 'solid',
                                                            borderWidth: '1px',
                                                            borderRadius: '4px',
                                                            position: 'absolute',
                                                            top: '-5px',
                                                            left: '0',
                                                            right: '0',
                                                            bottom: '0',
                                                            margin: '0',
                                                            padding: '0 8px',
                                                            overflow: 'hidden',
                                                            pointerEvents: 'none'

                                                        }}></fieldset>
                                                    </div>
                                                </Stack>
                                            </Container>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </div>
                    }
                    {selectedPlan.id!==0 &&
                        <div style={{marginTop: '50px'}}>
                            <Container maxWidth="sm">
                                <Stack spacing={3}>
                                {errorMessage !== null &&
                                    <Alert severity="error" onClose={() => setErrorMessage(null)}>{errorMessage}</Alert>
                                }
                                <Button color="success" size="large" variant="contained" disabled={selectedPlan.id===0||processing?true:false} onClick={e => {
                                    subscribe(e);
                                }}>{processing?(<><Loader /> {t('plans.processing')}</>):(<>{t('plans.activate')}</>)}</Button>
                                </Stack>
                            </Container>
                        </div>
                    }
                </Box>
            </Card>
            ):(
                <Alert severity="warning">{t('plans.noPlanFound')}</Alert>
            )}</>
            ):(
                <Alert severity="error" >{t('plans.accessDenied')}</Alert>
            )}</>
        ):(
            <Card sx={cardSx}>
                <Box p={3}>
                    <Loader text={t('plans.loadingPlans')} />
                </Box>
            </Card>

        )}
        </Container>

    )
}

export default Plans;
